import { FormEvent, useState } from 'react';
import { authApi } from '../core/api';
import type { SessionPayload } from '../core/contracts';
import { Brand } from '../ui/Brand';

type AuthMode = 'login' | 'signup';

function friendlyError(reason: unknown) {
  const message = reason instanceof Error ? reason.message : 'Unable to continue.';
  const labels: Record<string, string> = {
    invalid_credentials: 'Incorrect email or password.',
    email_exists: 'An account with this email already exists.',
    weak_password: 'Use a stronger password with at least 8 characters.',
  };
  return labels[message] || message;
}

export function AuthScreen({ onAuthenticated }: { onAuthenticated: (session: SessionPayload) => void }) {
  const [mode, setMode] = useState<AuthMode>('login');
  const [name, setName] = useState('');
  const [workspaceName, setWorkspaceName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  function switchMode(nextMode: AuthMode) {
    setMode(nextMode);
    setError('');
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!email.trim() || !password || (mode === 'signup' && (!name.trim() || !workspaceName.trim()))) {
      setError('Complete all required fields.');
      return;
    }
    if (mode === 'signup' && password.length < 8) {
      setError('Use at least 8 characters for your password.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      if (mode === 'signup') await authApi.register({ name: name.trim(), workspace_name: workspaceName.trim(), email: email.trim(), password });
      else await authApi.login(email.trim(), password);
      onAuthenticated(await authApi.me());
    } catch (reason) {
      setError(friendlyError(reason));
    } finally {
      setBusy(false);
    }
  }

  return <main className="auth-layout">
    <header className="auth-brand"><Brand /></header>
    <section className="auth-card" aria-labelledby="auth-title">
      <div className="auth-tabs" role="tablist" aria-label="Account access">
        <button className={mode === 'login' ? 'active' : ''} onClick={() => switchMode('login')} role="tab" aria-selected={mode === 'login'}>Sign in</button>
        <button className={mode === 'signup' ? 'active' : ''} onClick={() => switchMode('signup')} role="tab" aria-selected={mode === 'signup'}>Create account</button>
      </div>
      <div className="eyebrow">{mode === 'login' ? 'WELCOME BACK' : 'START YOUR WORKSPACE'}</div>
      <h1 id="auth-title">{mode === 'login' ? 'Sign in to Plessage' : 'Create your Plessage account'}</h1>
      <p>{mode === 'login' ? 'Run your content, conversations and team from one clear workspace.' : 'Set up a secure workspace for your team and connected accounts.'}</p>
      <form onSubmit={submit} noValidate>
        {mode === 'signup' && <label>Your name<input autoComplete="name" value={name} onChange={(event) => setName(event.target.value)} /></label>}
        {mode === 'signup' && <label>Workspace name<input autoComplete="organization" value={workspaceName} onChange={(event) => setWorkspaceName(event.target.value)} /></label>}
        <label>Email address<input type="email" inputMode="email" autoCapitalize="none" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} /></label>
        <label>Password<input type="password" autoComplete={mode === 'login' ? 'current-password' : 'new-password'} value={password} onChange={(event) => setPassword(event.target.value)} /></label>
        {error && <div className="form-error" role="alert">{error}</div>}
        <button className="primary-button" type="submit" disabled={busy}>{busy ? 'Please wait…' : mode === 'login' ? 'Sign in' : 'Create workspace'}</button>
      </form>
    </section>
  </main>;
}
